import { useCallback, useEffect, useRef, useState } from 'react';
import { useAppDispatch, useAppSelector } from '../../../Store/store';
import { TOASTER_STATE } from '../../../Model/Enums';
import { useLazyGetAccountsQuery, useLazyGetBanksQuery } from '../../../Store/aggreg8';
import { changeToasterState, isPopupBlocking, overLayerSelector, toggleOverLayer } from '../../../Store/layoutSlice';
import {
  currentCompanySelector,
  setAccountToCompanyListIsLoaded,
  userFlowInitResponseSelector,
} from '../../../Store/paramsBagSlice';

export const useAggreg8UserFlowWindow = () => {
  const [container, setContainer] = useState(null);
  const newWindow = useRef<Window>(window);

  const userFlowInitResponse = useAppSelector(userFlowInitResponseSelector);
  const currentCompany = useAppSelector(currentCompanySelector);
  const overLayerVisible = useAppSelector(overLayerSelector);

  const [getAccounts] = useLazyGetAccountsQuery();
  const [getBankList] = useLazyGetBanksQuery();

  const url =
    userFlowInitResponse?.token && `${process.env.REACT_APP_NON_AISP_SYNC_URL}?token=${userFlowInitResponse?.token}`;

  const dispatch = useAppDispatch();

  const handleAfterAddBank = useCallback(async () => {
    dispatch(toggleOverLayer(false));
    try {
      dispatch(setAccountToCompanyListIsLoaded(true));
      await getAccounts().unwrap();
      await getBankList().unwrap();
      dispatch(changeToasterState(TOASTER_STATE.AGGREG8_API_CONNECT_SUCCESS));
    } catch (e) {
      dispatch(changeToasterState(TOASTER_STATE.AGGREG8_API_CONNECT_FAILED_OR_TIMEOUT));
    } finally {
      dispatch(setAccountToCompanyListIsLoaded(false));
    }
    // eslint-disable-next-line
  }, [dispatch, currentCompany?._id]);

  const onWindowClose = useCallback(async () => {
    newWindow.current = null;
    await handleAfterAddBank();
  }, [handleAfterAddBank]);

  const checkWindowClosed = useCallback(async () => {
    await wait(1000);
    if (newWindow?.current?.closed) {
      await onWindowClose();
    } else {
      await checkWindowClosed();
    }
  }, [onWindowClose]);

  const wait = async (ms) => {
    return new Promise((resolve) => setTimeout(resolve, ms));
  };

  const handleClose = useCallback(async () => {
    await checkWindowClosed();
  }, [checkWindowClosed]);

  useEffect(() => {
    setContainer(document.createElement('div'));
  }, []);

  useEffect(() => {
    if (container && overLayerVisible && url) {
      const width = 450;
      const height = 725;
      const left = (window.screen.width - width) / 2;
      const top = (window.screen.height - height) / 4;

      try {
        const popup = window.open(
          url,
          'about',
          'resizable=no, toolbar=no, scrollbars=yes, menubar=no, width=' +
            width +
            ', height=' +
            height +
            ', top=' +
            top +
            ', left=' +
            left
        );
        if (popup === null || typeof popup === 'undefined') {
          dispatch(isPopupBlocking(true));
        } else {
          dispatch(isPopupBlocking(false));
        }
        newWindow.current = popup;
        newWindow?.current?.document?.body?.appendChild(container);
      } catch (e) {
        dispatch(isPopupBlocking(true));
      }

      if (window.focus) {
        newWindow?.current?.focus();
      }

      const handleWindowClose = async () => {
        await handleClose();
      };

      handleWindowClose().catch(console.error);

      const curWindow = newWindow.current;
      return () => {
        curWindow.close();
      };
    }
  }, [container, overLayerVisible, url, dispatch, handleClose, onWindowClose]);
  return { container, overLayerVisible };
};
